import { useCallback } from "react";
import { useSelector } from "react-redux";
import { RootState } from "src";
import { computeBookingPrice, computeBookingPriceDollars } from "src/lib/price";
import { usePricingConfig } from "./usePricingConfig";

/**
 * The displayed price.
 *
 * This used to reimplement the booking formula, and rounded the frequency
 * discount in a different place than the reducer did — so the subtotal shown
 * in the sidebar could be a dollar off the total actually charged. Both now
 * call the same function the server does.
 */
export function usePrice() {
  const state = useSelector((s: RootState) => s.bookingReducer);

  /**
   * Subscribes every consumer of this hook to the rates arriving from the API, so
   * a component that rendered against the bundled fallback updates once the real
   * config lands. Placed here rather than in each component because this hook is
   * already the single seam for displayed money.
   */
  usePricingConfig();

  const getSubtotal = useCallback(
    (): number => computeBookingPriceDollars(state).subTotal,
    [state]
  );

  const getTotal = useCallback(
    (): number => computeBookingPriceDollars(state).totalPrice,
    [state]
  );

  const getTax = useCallback(
    (): number => computeBookingPriceDollars(state).tax,
    [state]
  );

  /** The full cents breakdown, for surfaces that render itemised lines. */
  const getBreakdown = useCallback(() => computeBookingPrice(state), [state]);

  return { getSubtotal, getTotal, getTax, getBreakdown };
}
